import { Injectable } from '@angular/core';
import { Auth, GoogleAuthProvider, User as FirebaseUser, signInWithPopup, updateProfile } from '@angular/fire/auth';
import { Firestore, doc, getDoc, serverTimestamp, setDoc } from '@angular/fire/firestore';
import { AccountContextService } from './account-context.service';
import { AccountService } from './account.service';
import { AffiliateAttributionService } from './affiliate-attribution.service';
import { FirestoreService } from './firestore.service';

export interface OnboardingProfile {
  firstName?: string;
  lastName?: string;
  acceptedLegal?: boolean;
}

export interface ProvisionedUser {
  accountId: string;
  created: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthOnboardingService {
  constructor(
    private readonly auth: Auth,
    private readonly firestore: Firestore,
    private readonly accountService: AccountService,
    private readonly accountContextService: AccountContextService,
    private readonly firestoreService: FirestoreService,
    private readonly affiliateAttributionService: AffiliateAttributionService
  ) {}

  async signInWithGoogle(): Promise<FirebaseUser> {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const credential = await signInWithPopup(this.auth, provider);
    return credential.user;
  }

  async ensureUserProvisioned(user: FirebaseUser, profile: OnboardingProfile = {}): Promise<ProvisionedUser> {
    if (!user.emailVerified) {
      throw new Error('Verify your email address before completing account setup.');
    }

    const userRef = doc(this.firestore, `users/${user.uid}`);
    const existingSnapshot = await getDoc(userRef);
    if (existingSnapshot.exists()) {
      const accountId = this.readAccountId(existingSnapshot.data());
      if (accountId) {
        this.accountContextService.setActiveAccount(accountId);
        return { accountId, created: false };
      }
    }

    const names = this.resolveNames(user, profile);
    const displayName = `${names.firstName} ${names.lastName}`.trim() || user.displayName || user.email || 'YachtApp User';
    if (user.displayName !== displayName) {
      await updateProfile(user, { displayName });
    }

    const accountId = await this.accountService.createPersonalAccount({
      uid: user.uid,
      displayName,
      email: user.email
    });
    const userData = {
      uid: user.uid,
      firstName: names.firstName,
      lastName: names.lastName,
      fullName: displayName,
      displayName,
      email: user.email,
      photoUrl: user.photoURL ?? '',
      defaultAccountId: accountId,
      emailVerified: true,
      authProvider: user.providerData.some(provider => provider.providerId === 'google.com') ? 'google.com' : 'password',
      acceptedLegalAt: profile.acceptedLegal ? serverTimestamp() : null,
      createdAt: serverTimestamp()
    };

    await setDoc(userRef, userData, { merge: true });
    this.accountContextService.setActiveAccount(accountId);
    await Promise.allSettled([
      this.firestoreService.upsertPublicUserProfile(user.uid, userData),
      this.affiliateAttributionService.claimRegistration(accountId)
    ]);
    this.firestoreService.logUserActivity(user.uid, 'create-account', {
      email: user.email,
      firstName: names.firstName,
      lastName: names.lastName,
      accountId,
      authProvider: userData.authProvider
    }).subscribe({ error: err => console.error('Failed to log registration activity:', err) });

    return { accountId, created: true };
  }

  private readAccountId(data: Record<string, unknown>): string | null {
    const accountId = data['defaultAccountId'];
    return typeof accountId === 'string' && accountId.trim() ? accountId : null;
  }

  private resolveNames(user: FirebaseUser, profile: OnboardingProfile): { firstName: string; lastName: string } {
    if (profile.firstName?.trim() || profile.lastName?.trim()) {
      return {
        firstName: profile.firstName?.trim() ?? '',
        lastName: profile.lastName?.trim() ?? ''
      };
    }

    const parts = (user.displayName ?? '').trim().split(/\s+/).filter(Boolean);
    return {
      firstName: parts.shift() ?? '',
      lastName: parts.join(' ')
    };
  }
}
